/**
 * isFetching utility
 *
 * For all Store entities we keep `isFetching` flag.
 *
 * Thanks to that flag - we know whether an entity is being fetched.
 * For instance - knowing that, we can display a loading indicator.
 *
 * There is a tricky part. We support 3 states (values) for `isFetching`.
 * We know that the naming predispose for a boolean value,
 * but we support null / true / false.
 *
 * `null` - entity isn't fetched
 * `true` - entity being fetched
 * `false` - entity is fetched
 *
 * We did it in that way, because if we use it in a component (and we don't support `null`),
 * then its first value is `false`.
 * Therefore it misleads us that the fetching is completed and there isn't any data.
 *
 * Adding `null` as default value give us the knowledge that the fetching isn't started yet.
 *
 * Maybe you'll ask why we don't set `true` as default value? Good question!
 * In that way it will break our API fetching mechanism, because before fetching an entity
 * we're checking whether it is being fetching at the moment (in order to prevent duplicate calls).
 * @ref Please check `utils/redux/action.shouldFetch()`
 *
 * Reference: Jordan's fetching mechanism.
 * I hope he add it soon to his react project architecture repo.
 *
 */

/**
 * Is one of the entities being fetched
 *
 * @param {Array} entities
 */
const isFetchingEntities = entities => entities.filter(e => e.isFetching).length > 0

/**
 * Is fetching of entities triggered
 *
 * The fetching is triggered when all entities `isFetching` flag is false
 *
 * @param {Array} entities
 */
const isFetchingTriggered = entities => (
	entities.filter(e => e.isFetching === false).length === entities.length
)

/**
 * Are entities being fetched
 *
 * The first `isFetching` value is `null`.
 * Therefore we may to treat it as:
 * 'the fetching isn't triggered yet'.
 *
 * However, that doesn't suit us, because when we mount a component,
 * the first thing we want to do is to fetch all needed entities data.
 * Therefore we have to show a loading indicator.
 *
 * Because of this, we assume that using this function
 * we treat not triggered fetching as the fetching is being started,
 * because of the above reason (loading indicator).
 *
 * @param {Array} entities
 */
export const isFetching = (entities) => {
	return isFetchingEntities(entities) || !isFetchingTriggered(entities)
}



// WEBPACK FOOTER //
// ./src/utils/redux/fetching.js